import { ACCOUNT_BASE_NETWORK_FIELDS } from '../constants/accountNetworkRequestFields'
import { getCommaSeparatedCidrListError, getPrivateTgwSubnetsCidrListError, getSingleCidrError } from './cidrValidation'
import {
	buildIpAllocationsForSubmit,
	isIpAllocationsInputValid,
	normalizeIpAllocationsFromForm,
	type IpAllocationRowInput
} from './ipAllocationValidation'

export type AccountNetworkFormInput = {
	accountWorkloadType?: string
	vpcName?: string
	vpcCidrBlock?: string
	vpcAvailabilityZones?: string
	privateSubnetsCidr?: string
	tgwSubnetsCidr?: string
	requestTgwAttachment?: string
	isCoreVpc?: string
	createPrivateHostedZone?: string
	associateDnsResolverRules?: string
	useCentralizedVpcInterfaceEndpoints?: string
	privateHostedZoneName?: string
	addSecondaryCidrBlock?: boolean | string
	secondaryCidrBlock?: string
	addPublicCidrBlock?: boolean | string
	publicCidrBlock?: string
	ip_address_allocations?: IpAllocationRowInput[]
}

const trimValue = (value: unknown): string =>
	typeof value === 'string' ? value.trim() : String(value ?? '').trim()

const isTruthyFlag = (value: unknown): boolean =>
	value === true || trimValue(value).toLowerCase() === 'true'

export const isAccountEksWorkload = (accountWorkloadType: unknown): boolean =>
	trimValue(accountWorkloadType).toUpperCase() === 'EKS'

/**
 * Pod CIDR for the EKS custom CNI. Baselined EKS accounts carry it as a secondary CIDR
 * (e.g. `secondary_cidr_blocks = ["10.220.141.192/27", "100.64.0.0/16"]`), so approval both
 * pre-fills it and refuses to submit an EKS account without it.
 */
export const EKS_POD_SECONDARY_CIDR = '100.64.0.0/16'

export const EKS_POD_SECONDARY_CIDR_ERROR = `EKS accounts must include ${EKS_POD_SECONDARY_CIDR} in the secondary CIDR block (pod range for the custom CNI).`

const splitCidrList = (value: unknown): string[] =>
	String(value ?? '')
		.split(',')
		.map((entry) => entry.trim())
		.filter(Boolean)

export function hasEksPodSecondaryCidr(value: unknown): boolean {
	return splitCidrList(value).some(
		(entry) => entry.toLowerCase() === EKS_POD_SECONDARY_CIDR
	)
}

/** Append the pod CIDR unless already listed; preserves any existing entries. */
export function withEksPodSecondaryCidr(value: unknown): string {
	const entries = splitCidrList(value)
	if (hasEksPodSecondaryCidr(entries.join(','))) return entries.join(', ')
	return [...entries, EKS_POD_SECONDARY_CIDR].join(', ')
}

/**
 * True once the allocation table carries at least one CIDR. The account baseline appends those
 * ranges to `private_subnets_cidr` downstream (see any EKS baseline: the Shared-Workernode /
 * Workernode / Pods entries come from here), so Private Subnets CIDR need not repeat them.
 */
export function ipAllocationsHaveInput(
	rows: IpAllocationRowInput[] | undefined
): boolean {
	return (rows ?? []).some(
		(row) =>
			Boolean(trimValue(row?.cidrAzA)) ||
			Boolean(trimValue(row?.cidrAzB)) ||
			Boolean(trimValue(row?.cidrAzC))
	)
}

/** EKS accounts must carry the pod range; non-EKS is unconstrained. */
export function getEksPodSecondaryCidrError(
	accountWorkloadType: unknown,
	secondaryCidrBlock: unknown
): string | undefined {
	if (!isAccountEksWorkload(accountWorkloadType)) return undefined
	return hasEksPodSecondaryCidr(secondaryCidrBlock)
		? undefined
		: EKS_POD_SECONDARY_CIDR_ERROR
}

const MULTI_CIDR_NETWORK_FIELDS = ['privateSubnetsCidr', 'tgwSubnetsCidr'] as const

export const PRIVATE_HOSTED_ZONE_SUFFIX = '.globetel.cloud'

export const PRIVATE_HOSTED_ZONE_NAME_ERROR = `Private Hosted Zone Name must end with ${PRIVATE_HOSTED_ZONE_SUFFIX} (e.g. example-dv${PRIVATE_HOSTED_ZONE_SUFFIX}).`

export const PRIVATE_HOSTED_ZONE_NAME_PLACEHOLDER = `example-dv${PRIVATE_HOSTED_ZONE_SUFFIX}`

export function getPrivateHostedZoneNameError(value: unknown): string | null {
	const trimmed = trimValue(value)
	if (!trimmed) return null

	const lower = trimmed.toLowerCase()
	if (!lower.endsWith(PRIVATE_HOSTED_ZONE_SUFFIX)) {
		return PRIVATE_HOSTED_ZONE_NAME_ERROR
	}

	const prefix = lower.slice(0, -PRIVATE_HOSTED_ZONE_SUFFIX.length)
	if (!prefix || prefix.endsWith('.')) {
		return PRIVATE_HOSTED_ZONE_NAME_ERROR
	}

	return null
}

export const validateAccountNetworkDetails = (
	input: AccountNetworkFormInput,
	isProduction: boolean,
	ipRows?: IpAllocationRowInput[]
): boolean => {
	const ipAllocationsRequired = isAccountEksWorkload(input.accountWorkloadType)
	const rows =
		ipRows ??
		(normalizeIpAllocationsFromForm(
			input.ip_address_allocations,
			isProduction,
			ipAllocationsRequired
		) as IpAllocationRowInput[])

	// Private Subnets CIDR stops being mandatory once the allocations carry CIDRs — the account
	// baseline appends those to private_subnets_cidr, so requiring both duplicates the input.
	const privateSubnetsOptional = ipAllocationsHaveInput(rows)

	for (const { key } of ACCOUNT_BASE_NETWORK_FIELDS) {
		if (key === 'privateSubnetsCidr' && privateSubnetsOptional) continue
		if (!trimValue(input[key as keyof AccountNetworkFormInput])) {
			return false
		}
	}

	const vpcCidr = trimValue(input.vpcCidrBlock)
	if (getSingleCidrError(vpcCidr)) {
		return false
	}

	for (const key of MULTI_CIDR_NETWORK_FIELDS) {
		const value = trimValue(input[key as keyof AccountNetworkFormInput])
		if (getPrivateTgwSubnetsCidrListError(value)) {
			return false
		}
	}

	if (getPrivateHostedZoneNameError(input.privateHostedZoneName)) {
		return false
	}

	if (!isIpAllocationsInputValid(rows, isProduction, ipAllocationsRequired)) {
		return false
	}

	if (isTruthyFlag(input.addSecondaryCidrBlock)) {
		const secondaryCidr = trimValue(input.secondaryCidrBlock)
		if (!secondaryCidr || getCommaSeparatedCidrListError(secondaryCidr)) {
			return false
		}
	}

	// EKS needs the pod range regardless of the checkbox — unticking it would drop the CIDR
	// from the payload entirely.
	if (getEksPodSecondaryCidrError(input.accountWorkloadType, input.secondaryCidrBlock)) {
		return false
	}

	if (isTruthyFlag(input.addPublicCidrBlock)) {
		const publicCidr = trimValue(input.publicCidrBlock)
		if (!publicCidr || getCommaSeparatedCidrListError(publicCidr)) {
			return false
		}
	}

	return true
}

export const buildAccountNetworkDetailsPayload = (
	formValues: Record<string, unknown>,
	isProduction: boolean
): Record<string, unknown> => {
	const accountWorkloadType = trimValue(formValues.accountWorkloadType) || 'Non-EKS'
	const ipAllocationsRequired = isAccountEksWorkload(accountWorkloadType)
	const payload: Record<string, unknown> = {}

	for (const { key } of ACCOUNT_BASE_NETWORK_FIELDS) {
		payload[key] = trimValue(formValues[key])
	}

	payload.ip_address_allocations = buildIpAllocationsForSubmit(
		normalizeIpAllocationsFromForm(
			formValues.ip_address_allocations,
			isProduction,
			ipAllocationsRequired
		),
		isProduction,
		ipAllocationsRequired
	)
	payload.addSecondaryCidrBlock = isTruthyFlag(formValues.addSecondaryCidrBlock)
	payload.secondaryCidrBlock = payload.addSecondaryCidrBlock
		? trimValue(formValues.secondaryCidrBlock)
		: ''
	payload.addPublicCidrBlock = isTruthyFlag(formValues.addPublicCidrBlock)
	payload.publicCidrBlock = payload.addPublicCidrBlock
		? trimValue(formValues.publicCidrBlock)
		: ''

	return payload
}
